/**
 * Canonical admin allowlist for the RidePulse web admin panel.
 *
 * This is the single source of truth for which authenticated users may see or
 * enter admin routes. It mirrors the server-side `is_web_admin()` allowlist
 * in the shared Supabase backend (RidePulse issue #325, Stage 5 — live).
 *
 * Product rule (binding):
 *   Only admin@ridepulse.app and support@ridepulse.app are full administrators.
 *   No @ridepulse.app domain-wide check is acceptable in any privileged path.
 *
 * Server enforcement remains authoritative. This module is presentation /
 * route-gating only — it prevents misleading UI states, not unauthorized data
 * access. The server independently rejects unauthorized RPC and RLS access.
 */

const ADMIN_EMAILS: ReadonlySet<string> = new Set([
  'admin@ridepulse.app',
  'support@ridepulse.app',
]);

/**
 * Returns true only for the two approved admin accounts.
 * Case-insensitive: emails are normalized to lowercase before comparison.
 */
export const isAdminEmail = (email?: string | null): boolean => {
  if (!email) return false;
  return ADMIN_EMAILS.has(email.trim().toLowerCase());
};

/**
 * The canonical list, exposed for tests and diagnostics.
 * Do not use this to build a second divergent allowlist — always call
 * `isAdminEmail` instead.
 */
export const ADMIN_ALLOWLIST: readonly string[] = Object.freeze(Array.from(ADMIN_EMAILS));
